import {
  UnifiedProfileQuery,
  UnifiedProfileQuery$data,
} from 'graphql/__generated__/UnifiedProfileQuery.graphql';
import { useLazyLoadQuery, readInlineData } from 'react-relay';
import TopPanel from './top-panel/TopPanel';
import MainPanel from './main-panel/MainPanel';
import SidePanel from './side-panel/SidePanel';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { UNIFIED_PROFILE_QUERY } from 'graphql/UnifiedProfileQuery';
import { ErrorBoundary } from 'react-error-boundary';
import Fallback from 'components/common/fallback/Fallback';
import { logBoundaryError } from 'services/logger';
import { Redirect } from 'react-resource-router';
import { MOBILE, DESKTOP, TABLET } from '../../constants/breakpoints';
import CoverPhoto from 'assets/images/cover-photo.svg';
import useIsPersonalView from 'utils/useIsPersonalView';
import { UnifiedProfileFragment$key } from 'graphql/__generated__/UnifiedProfileFragment.graphql';
import { UNIFIED_PROFILE_FRAGMENT } from 'graphql/UnifiedProfileFragment';
import { useSendScreenEventEffect } from 'hooks/useScreenEvent';
import { trackProfileScreenView } from 'analytics/profileAnalytics';
import PrivateProfileComponent from './private-profile/Wrapper';

const ProfileComponent = ({ internalId }: { internalId: string }) => {
  const unifiedProfileData = useLazyLoadQuery<UnifiedProfileQuery>(
    UNIFIED_PROFILE_QUERY,
    { internalId },
  );

  const unifiedProfile = unifiedProfileData?.unified?.unifiedProfile;

  const profileData =
    unifiedProfile && `__fragments` in unifiedProfile
      ? readInlineData<UnifiedProfileFragment$key>(
          UNIFIED_PROFILE_FRAGMENT,
          unifiedProfile as NonNullable<
            UnifiedProfileQuery$data['unified']
          >['unifiedProfile'],
        )
      : null;

  const isPersonalView = useIsPersonalView(internalId);

  useSendScreenEventEffect(() => trackProfileScreenView(isPersonalView), {
    shouldSendEvent: Boolean(unifiedProfile),
  });

  // if a linked profile is tried to be visited then redirect to the primary profile
  if (profileData?.internalId !== internalId) {
    return <Redirect to={`/profile/${profileData?.internalId}`} />;
  }

  if (!unifiedProfile) {
    return <Redirect to="/error/user_not_found" />;
  }

  return (
    <PrivateProfileComponent>
      <div>
        <CoverImage />
        <MainContentContainer>
          <ErrorBoundary
            FallbackComponent={Fallback}
            onError={logBoundaryError}
          >
            <TopPanel />
          </ErrorBoundary>
          <MainContent>
            <div css={mainPanelStyles}>
              <ErrorBoundary
                FallbackComponent={Fallback}
                onError={logBoundaryError}
              >
                <MainPanel data={unifiedProfileData} />
              </ErrorBoundary>
            </div>
            <aside css={sidePanelStyles}>
              <ErrorBoundary
                FallbackComponent={Fallback}
                onError={logBoundaryError}
              >
                <SidePanel />
              </ErrorBoundary>
            </aside>
          </MainContent>
        </MainContentContainer>
      </div>
    </PrivateProfileComponent>
  );
};
export default ProfileComponent;

export const MOBILE_COVER_IMAGE_HEIGHT = 175;
export const TABLET_COVER_IMAGE_HEIGHT = 194;
export const DESKTOP_COVER_IMAGE_HEIGHT = 275;

export const MainContentContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <div css={mainContentContainerStyles}>{children}</div>;
};
const mainContentContainerStyles = css({
  // with padding this corresponds to 1232
  maxWidth: '1266px',
  marginTop: token('space.300'),
  marginRight: 'auto',
  marginLeft: 'auto',
  marginBottom: '0px',
  padding: '0 16px',
  [`@media (min-width: ${MOBILE.MAX_WIDTH}px) and (max-width: ${DESKTOP.MIN_WIDTH}px)`]:
    {
      // with padding this corresponds to 720
      width: '754px',
    },
  [`@media (min-width: ${MOBILE.MAX_WIDTH}px)`]: {
    margin: `${token('space.200')} auto`,
    marginTop: token('space.200'),
    marginRight: 'auto',
    marginLeft: 'auto',
    marginBottom: '0px',
  },
});

export const CoverImage = () => {
  return <img css={coverImageStyles} src={CoverPhoto} alt="cover-image" />;
};
const coverImageStyles = css({
  display: 'block',
  width: '100%',
  objectFit: 'cover',
  position: 'absolute',
  top: '0',
  height: `${MOBILE_COVER_IMAGE_HEIGHT}px`,
  [`@media (min-width: ${TABLET.MIN_WIDTH}px)`]: {
    height: `${TABLET_COVER_IMAGE_HEIGHT}px`,
  },
  [`@media (min-width: ${DESKTOP.MIN_WIDTH}px)`]: {
    height: `${DESKTOP_COVER_IMAGE_HEIGHT}px`,
  },
});

export const MainContent = ({ children }: { children: React.ReactNode }) => {
  return <div css={mainContentStyles}>{children}</div>;
};
const mainContentStyles = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: token('space.600'),
  [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
    gap: token('space.400'),
  },
  [`@media (min-width: ${DESKTOP.MIN_WIDTH}px)`]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
});

const mainPanelStyles = css({
  width: '100%',
  order: 2,
  [`@media (min-width: ${DESKTOP.MIN_WIDTH}px)`]: {
    order: 1,
    width: '854px',
  },
});

const sidePanelStyles = css({
  order: 1,
  width: '100%',
  [`@media (min-width: ${DESKTOP.MIN_WIDTH}px)`]: {
    order: 2,
    width: '355px',
  },
});
